import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    FormControl,
    FormGroup,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QuizIcon from '@mui/icons-material/Quiz';

const QuizDialog = ({ postId, open, onClose, onComplete }) => {
    const [quiz, setQuiz] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);

    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchQuiz = async () => {
            if (!postId || !open) {
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await fetch(`${apiUrl}posts/post_quiz/${postId}/`);
                const data = await response.json();

                // -1 means no quiz
                if (data === -1) {
                    setQuiz(null);
                    // No quiz, proceed immediately
                    if (onComplete) {
                        onComplete(null);
                    }
                } else {
                    setQuiz(data);
                    // Initialize answers object
                    const initialAnswers = {};
                    if (data.questions) {
                        data.questions.forEach(q => {
                            initialAnswers[q.id] = q.type === 'checkbox' ? [] : '';
                        });
                    }
                    setAnswers(initialAnswers);
                }
            } catch (err) {
                console.error('Error fetching quiz:', err);
                setError('Failed to load quiz');
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchQuiz();
        }
    }, [postId, open, apiUrl, onComplete]);

    const handleAnswerChange = (questionId, value, questionType) => {
        let newAnswers;

        if (questionType === 'checkbox') {
            const currentAnswers = answers[questionId] || [];
            if (currentAnswers.includes(value)) {
                newAnswers = { ...answers, [questionId]: currentAnswers.filter(v => v !== value) };
            } else {
                newAnswers = { ...answers, [questionId]: [...currentAnswers, value] };
            }
        } else {
            newAnswers = { ...answers, [questionId]: value };
        }

        setAnswers(newAnswers);
        // Clear validation error for this question
        setValidationErrors(prev => prev.filter(id => id !== questionId));
    };

    const validateQuiz = () => {
        if (!quiz || !quiz.questions) return true;

        const errors = [];
        quiz.questions.forEach(q => {
            if (q.required) {
                const answer = answers[q.id];
                const isEmpty =
                    answer === '' ||
                    answer === null ||
                    answer === undefined ||
                    (Array.isArray(answer) && answer.length === 0);

                if (isEmpty) {
                    errors.push(q.id);
                }
            }
        });

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSubmit = () => {
        if (validateQuiz()) {
            // Format answers with question labels for better readability
            const formattedAnswers = {};
            if (quiz && quiz.questions) {
                quiz.questions.forEach(q => {
                    formattedAnswers[q.id] = answers[q.id];
                });
            }

            console.log('Quiz Answers Submitted:', JSON.stringify(formattedAnswers, null, 2));

            if (onComplete) {
                onComplete({ answers: formattedAnswers, rawAnswers: answers });
            }
            onClose();
        }
    };

    const renderQuestion = (question, index) => {
        const { id, type, label, options, required } = question;
        const hasError = validationErrors.includes(id);

        return (
            <Box
                key={id}
                sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    border: hasError ? '1px solid #f44336' : '1px solid #e0e0e0',
                    backgroundColor: hasError ? '#fff5f5' : '#fafafa'
                }}
            >
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: hasError ? '#f44336' : '#000',
                        mb: 1.5
                    }}
                >
                    {index + 1}. {label} {required && <span style={{ color: 'red' }}>*</span>}
                </Typography>

                {/* Text Input */}
                {type === 'text' && (
                    <TextField
                        fullWidth
                        size="small"
                        value={answers[id] || ''}
                        onChange={(e) => handleAnswerChange(id, e.target.value, type)}
                        placeholder="Votre réponse..."
                        error={hasError}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#fff'
                            }
                        }}
                    />
                )}

                {/* Textarea */}
                {type === 'textarea' && (
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={answers[id] || ''}
                        onChange={(e) => handleAnswerChange(id, e.target.value, type)}
                        placeholder="Votre réponse..."
                        error={hasError}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#fff'
                            }
                        }}
                    />
                )}

                {/* Number Input */}
                {type === 'number' && (
                    <TextField
                        type="number"
                        size="small"
                        value={answers[id] || ''}
                        onChange={(e) => handleAnswerChange(id, e.target.value, type)}
                        placeholder="0"
                        error={hasError}
                        sx={{
                            width: 150,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#fff'
                            }
                        }}
                    />
                )}

                {/* Select Dropdown */}
                {type === 'select' && options && (
                    <FormControl fullWidth size="small" error={hasError}>
                        <Select
                            value={answers[id] || ''}
                            onChange={(e) => handleAnswerChange(id, e.target.value, type)}
                            displayEmpty
                            sx={{
                                borderRadius: 2,
                                backgroundColor: '#fff'
                            }}
                        >
                            <MenuItem value="" disabled>Sélectionnez une option</MenuItem>
                            {options.map((option, idx) => (
                                <MenuItem key={idx} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Radio Buttons */}
                {type === 'radio' && options && (
                    <RadioGroup
                        value={answers[id] || ''}
                        onChange={(e) => handleAnswerChange(id, e.target.value, type)}
                    >
                        {options.map((option, idx) => (
                            <FormControlLabel
                                key={idx}
                                value={option}
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            color: hasError ? '#f44336' : '#0017E7',
                                            '&.Mui-checked': { color: '#0017E7' }
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>{option}</Typography>}
                            />
                        ))}
                    </RadioGroup>
                )}

                {/* Checkboxes */}
                {type === 'checkbox' && options && (
                    <FormGroup>
                        {options.map((option, idx) => (
                            <FormControlLabel
                                key={idx}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={(answers[id] || []).includes(option)}
                                        onChange={() => handleAnswerChange(id, option, type)}
                                        sx={{
                                            color: hasError ? '#f44336' : '#0017E7',
                                            '&.Mui-checked': { color: '#0017E7' }
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>{option}</Typography>}
                            />
                        ))}
                    </FormGroup>
                )}

                {hasError && (
                    <Typography sx={{ color: '#f44336', fontSize: '12px', mt: 1, fontFamily: 'Poppins' }}>
                        Cette question est obligatoire
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '15px',
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QuizIcon sx={{ color: '#0017E7' }} />
                    <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '18px', color: '#0017E7' }}>
                        Quiz de candidature
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                {!loading && !quiz && (
                    <Typography sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                        Aucun quiz disponible pour cette offre.
                    </Typography>
                )}

                {!loading && quiz && (
                    <>
                        <Typography sx={{ fontFamily: 'Poppins', fontSize: '14px', color: '#666', mb: 3 }}>
                            Veuillez répondre aux questions suivantes avant de soumettre votre candidature.
                        </Typography>

                        {validationErrors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Veuillez répondre à toutes les questions obligatoires.
                            </Alert>
                        )}

                        {quiz?.questions?.map((question, index) => renderQuestion(question, index))}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontFamily: 'Poppins'
                    }}
                >
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !quiz}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        backgroundColor: '#0017E7',
                        '&:hover': {
                            backgroundColor: '#0012B3'
                        }
                    }}
                >
                    Soumettre et postuler
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuizDialog;
